import { defineStore } from 'pinia';
import { useApi } from 'src/composables/useApi';
import { api } from 'src/boot/axios';
import { Notify } from 'quasar';
import { isAxiosError } from 'axios';
import { Router } from 'vue-router';
import { ref, computed } from 'vue';

export interface User {
  id: number;
  school_id: number | null;
  name: string;
  email: string;
  job_title: string | null;
  email_verified_at: string | null; // Datum en tijd in ISO-formaat als string
  invite_accepted_at: string | null;
  created_at: string; // Datum en tijd in ISO-formaat
  updated_at: string; // Datum en tijd in ISO-formaat
  is_admin: boolean;
  is_school_user: boolean;
  initials: string;
  role: string;
  role_label: string;
  permissions: string[]; // Array van strings voor de rechten/permissions
  is_impersonated: boolean;
  was_admin_id: number | null;
}

const { post } = useApi();

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const userId = computed(() =>
    user.value?.was_admin_id ? user.value?.was_admin_id : user.value?.id,
  );

  function setUser(newUser: User) {
    user.value = newUser;
  }

  async function logout() {
    user.value = null;
    await post('logout', {}, {}, false);
    Notify.create({
      type: 'positive',
      multiLine: true,
      message: 'Logged out',
      position: 'top',
    });
  }

  async function updateUser() {
    try {
      const { data } = await api.get('me');
      user.value = data.data;
    } catch (error) {
      console.log(error);
    }
  }

  async function loginAs(id: number, router: Router, routeName: string) {
    try {
      const response = await post('login-as', { id: id });
      const data = response.data;
      setUser(data);

      Notify.create({
        type: 'positive',
        multiLine: true,
        message:
          'Logged in as email: ' +
          data.email +
          ' with role: ' +
          data.role_label,
        position: 'top',
      });
      router.push({ name: routeName });
      return data;
    } catch (err) {
      if (isAxiosError(err)) {
        Notify.create({
          type: 'negative',
          multiLine: true,
          message: err.response?.data?.message || 'Unknown error',
          position: 'top',
        });
      } else {
        Notify.create({
          type: 'negative',
          multiLine: true,
          message: 'Unknown error',
          position: 'top',
        });
      }
      throw err;
    }
  }

  return {
    user,
    userId,
    setUser,
    logout,
    updateUser,
    loginAs,
  };
});
